import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';

import { cn } from '@utils/index';

import type { StepCell } from '../constants/steps';
import { W2_SELECT_TID } from '../constants/testids';

interface W2SelectionStepBarProps {
  steps: StepCell[];
  /** Jump back to an already-completed step (no-op on active/future steps). */
  onStepClick?: (index: number) => void;
}

/**
 * The bottom "Step N | Name" progress bar — a presentational copy of the B2C
 * `Footer` (rounded-full white bar, per-step checkmark / animated dots / muted
 * future, horizontal-scroll affordances) driven purely by the `stepModel`
 * projection. Desktop-only (`max-2xl:hidden`), matching the B2C footer. Only
 * completed steps are clickable (jump-back), mirroring the footer.
 */
export const W2SelectionStepBar: React.FC<W2SelectionStepBarProps> = ({
  steps,
  onStepClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, [steps]);

  const scrollBy = (delta: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    // `shrink-0` so the bar keeps its full height as the bottom rail of the
    // fixed-viewport `SelectionScaffold` shell instead of being squeezed by a
    // tall scrolling <main>.
    <footer className="w-full shrink-0 max-2xl:hidden">
      <div className="mx-auto max-w-[1920px] px-6">
        <div className="relative mx-auto flex h-16 w-full items-center rounded-full border-gray-200 bg-white px-6 text-sm text-gray-600">
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollBy(-200)}
              className="absolute left-0 z-10 rounded-full bg-white p-2 shadow-md hover:bg-gray-100"
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          <div
            ref={scrollRef}
            data-testid={W2_SELECT_TID.stepbar.root}
            className="scrollbar-hide flex w-full overflow-x-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            <div className="flex w-full justify-between gap-4 sm:gap-8">
              {steps.map((step, i) => {
                const isCompleted = step.state === 'completed';
                const isActive = step.state === 'active';
                const isClickable = isCompleted && !!onStepClick;
                return (
                  <div
                    key={`${step.label}-${i}`}
                    data-testid={`${W2_SELECT_TID.stepbar.step}-${i}`}
                    data-state={step.state}
                    className={cn(
                      'flex items-center gap-1 whitespace-nowrap',
                      isCompleted && 'text-primary',
                      isActive && 'font-semibold text-black',
                      isClickable &&
                        'cursor-pointer transition-opacity hover:opacity-80'
                    )}
                    onClick={isClickable ? () => onStepClick?.(i) : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onStepClick?.(i);
                      }
                    }}
                    aria-label={
                      isClickable ? `Go back to ${step.label}` : undefined
                    }
                  >
                    <span className="pr-3 text-xs font-normal text-[#a6a5a0]">
                      Step {i + 1}
                    </span>
                    <span className="text-xs text-[#a6a5a0]">|</span>
                    <span
                      className={cn(
                        'pl-3 text-base',
                        step.state === 'future' && 'text-[#a6a5a0]'
                      )}
                    >
                      {step.label}
                    </span>
                    {isCompleted && (
                      <span className="font-black text-primary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          viewBox="0 0 49 49.89"
                          width={20}
                          height={20}
                          aria-hidden="true"
                        >
                          <polyline
                            fill="none"
                            stroke="#710036"
                            style={{ stroke: 'var(--color-primary)' }}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="6"
                            points="17.08 26.72 23.53 33.83 35.08 16.06"
                          />
                        </svg>
                      </span>
                    )}
                    {isActive && (
                      <span className="ml-1">
                        <AnimatedDots />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollBy(200)}
              className="absolute right-0 z-10 rounded-full bg-white p-2 shadow-md hover:bg-gray-100"
              aria-label="Scroll right"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </footer>
  );
};
